import {useAuthenticationContext} from "../../../../utils/auth/AuthenticationContext";
import {formatDistanceToNow, parseISO} from "date-fns";
import {Link, useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import * as icons from "@fortawesome/free-solid-svg-icons"
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import "./UnreadCommentsComponent.scss";
import FlintButton from "../../../controls/FlintButton";
import {useFlintDialogContext} from "../dialogs/FlintDialogContextProvider";
import {CommentNotificationDTO} from "../../../../utils/signalr/signalr.models";
import {useFlintNotificationContext} from "../notifications/FlintNotificationContext";
import {useState} from "react";
import {VivoPortal} from "../../../../utils/AppConstants";
import EnumSegment = VivoPortal.EnumSegment;

export default function UnreadCommentsComponent() {
    const {lng} = useParams<{lng: string}>();
    const {t} = useTranslation();
    const {comments, currentIndex, isLoading, handlers} = useUnreadCommentsComponent();

    if (comments.get.length === 0)
        return (
            <div className={"comment-empty"}>
                <FontAwesomeIcon icon={icons.faBell} className={"comment-empty-icon"}/>
                <div className={"mb-3"}>{t("COMMENTS_HISTORY_NoNew")}</div>
                <FlintButton
                    onClick={handlers.handleUnhideComments}
                    fillMode={"outline"}
                    themeColor={"warning"}
                    size={"small"}
                    disabled={isLoading}
                    title={t("COMMENTS_HISTORY_RecoverHidden_Hint")}>
                    <FontAwesomeIcon icon={icons.faRotateLeft} className={"me-2"}/>
                    {isLoading ? <FontAwesomeIcon icon={icons.faSpinner} spin/> : t("COMMENTS_HISTORY_RecoverHidden")}
                </FlintButton>
            </div>
        );

    const comment = comments.get[currentIndex];
    const total = comments.get.length;

    return (
        <div id={"unread-comments"}>
            {/* scrollable area: navigation + comment card */}
            <div className={"comment-main"}>
                <div className={"comment-nav"}>
                    <button
                        className={"comment-nav-btn"}
                        onClick={() => handlers.handleNavigate(currentIndex - 1)}
                        disabled={currentIndex === 0 || isLoading}
                        aria-label="Previous comment">
                        <FontAwesomeIcon icon={icons.faChevronLeft}/>
                    </button>
                    <span className={"comment-nav-counter"}>{currentIndex + 1} / {total}</span>
                    <button
                        className={"comment-nav-btn"}
                        onClick={() => handlers.handleNavigate(currentIndex + 1)}
                        disabled={currentIndex >= total - 1 || isLoading}
                        aria-label="Next comment">
                        <FontAwesomeIcon icon={icons.faChevronRight}/>
                    </button>
                </div>

                <CommentCard comment={comment} lng={lng!}/>
            </div>

            {/* sticky action footer */}
            <div className={"comment-actions"}>
                <div className={"comment-actions-row"}>
                    <FlintButton
                        themeColor={"primary"}
                        onClick={handlers.handleHideToCurrent}
                        disabled={isLoading}
                        title={t("COMMENTS_HISTORY_HideOlder_Hint")}>
                        <FontAwesomeIcon icon={isLoading ? icons.faSpinner : icons.faCircleCheck} spin={isLoading} className={"me-2"}/>
                        {t("COMMENTS_HISTORY_HideOlder")}
                    </FlintButton>
                    <FlintButton
                        onClick={handlers.handleHideAll}
                        themeColor={"error"}
                        fillMode={"outline"}
                        disabled={isLoading}
                        title={t("COMMENTS_HISTORY_HideAll_Hint")}>
                        <FontAwesomeIcon icon={isLoading ? icons.faSpinner : icons.faEyeSlash} spin={isLoading} className={"me-2"}/>
                        {t("COMMENTS_HISTORY_HideAll")}
                    </FlintButton>
                </div>
                <FlintButton
                    onClick={handlers.handleUnhideComments}
                    className={"w-100"}
                    fillMode={"outline"}
                    themeColor={"warning"}
                    disabled={isLoading}
                    title={t("COMMENTS_HISTORY_RecoverHidden_Hint")}>
                    <FontAwesomeIcon icon={isLoading ? icons.faSpinner : icons.faRotateLeft} spin={isLoading} className={"me-2"}/>
                    {t("COMMENTS_HISTORY_RecoverHidden")}
                </FlintButton>
            </div>
        </div>
    );
}

interface CommentCardProps {
    comment: CommentNotificationDTO;
    lng: string;
}

function CommentCard({comment: c, lng}: CommentCardProps) {
    const {t} = useTranslation();
    const divisionLabel = getDivisionLabel(c.Division, t);
    const relativeDate = formatDistanceToNow(parseISO(c.CreatedOnUtc?.toString() + 'Z'), {addSuffix: true});

    return (
        <div className={"comment-card"}>
            <div className={"comment-card-meta"}>
                {divisionLabel && (
                    <span className={"comment-division-badge"}>{divisionLabel}</span>
                )}
                {c.CreatedByOrderOwner && (
                    <span className={"comment-owner-badge"}>
                        <FontAwesomeIcon icon={icons.faCrown} className={"me-1"}/>
                        {t("COMMENTS_HISTORY_OrderOwner")}
                    </span>
                )}
            </div>
            <div className={"comment-author"}>
                <FontAwesomeIcon icon={icons.faUser} className={"me-1"}/>
                <strong>{c.AuthorFullName}</strong>
                <span className={"comment-date ms-2"} title={c.CreatedOnUtc?.toString()}>{relativeDate}</span>
            </div>
            <div className={"alert alert-light text-wrap overflow-auto comment-text"}
                 dangerouslySetInnerHTML={{__html: c.Text}}/>
            <div className={"comment-order-link"}>
                <FontAwesomeIcon icon={icons.faFileLines} className={"me-1"}/>
                <Link className={"me-2"} to={`/${lng}/orders/preview/${c.OrderGuid}`}>{c.OrderFullNumber}</Link>
                <Link to={`/${lng}/orders/preview/${c.OrderGuid}`} target="_blank">
                    <FontAwesomeIcon icon={icons.faExternalLink}/>
                </Link>
            </div>
        </div>
    );
}

function getDivisionLabel(division: EnumSegment, t: (key: string) => string): string {
    switch (division) {
        case EnumSegment.PaperAndBoard:      return t("PaperAndBoard");
        case EnumSegment.Sheetfed:           return t("SheetFed");
        case EnumSegment.Narrow_Web:         return t("Narrow_Web");
        case EnumSegment.Flexible_Packaging: return t("Flexible_Packaging");
        default:                             return "";
    }
}

function useUnreadCommentsComponent() {
    const {comments} = useAuthenticationContext();
    const {showAreYouSureDialog} = useFlintDialogContext();
    const {showNotification} = useFlintNotificationContext();
    const {t} = useTranslation();
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isLoading, setIsLoading] = useState(false);

    function handleNavigate(index: number) {
        const clamped = Math.max(0, Math.min(index, comments.get.length - 1));
        setCurrentIndex(clamped);
    }

    function getCurrentComment(): CommentNotificationDTO | null {
        return comments.get[currentIndex] ?? null;
    }

    async function handleHideToCurrent() {
        const commentToHide = getCurrentComment();
        if (!commentToHide) return;

        if (currentIndex > 0) {
            const question = <span dangerouslySetInnerHTML={{__html: t("COMMENTS_HISTORY_ConfirmHideN", {count: currentIndex + 1})}}/>;
            return showAreYouSureDialog({
                content: question,
                onAccept: async () => {
                    setIsLoading(true);
                    try {
                        await comments.hideOlderComments(commentToHide);
                        setCurrentIndex(0);
                        showNotification(t("COMMENTS_HISTORY_Success"), {style: "success"});
                    } finally {
                        setIsLoading(false);
                    }
                }
            });
        }

        setIsLoading(true);
        try {
            await comments.hideOlderComments(commentToHide);
            setCurrentIndex(0);
            showNotification(t("COMMENTS_HISTORY_Success"), {style: "success"});
        } finally {
            setIsLoading(false);
        }
    }

    function handleHideAll() {
        const lastComment = comments.get[comments.get.length - 1];
        if (!lastComment) return;
        const question = <span className={"color-flint-red"} dangerouslySetInnerHTML={{__html: t("COMMENTS_HISTORY_ConfirmHideAll", {count: comments.get.length})}}/>;
        showAreYouSureDialog({
            content: question,
            onAccept: async () => {
                setIsLoading(true);
                try {
                    await comments.hideOlderComments(lastComment);
                    setCurrentIndex(0);
                    showNotification(t("COMMENTS_HISTORY_Success"), {style: "success"});
                } finally {
                    setIsLoading(false);
                }
            }
        });
    }

    async function handleUnhideComments() {
        setIsLoading(true);
        try {
            const result = await comments.unhideHiddenComments();
            setCurrentIndex(0);
            if (!result) {
                showNotification(t("COMMENTS_HISTORY_RecoverNone"), {style: "info"});
            } else {
                showNotification(t("COMMENTS_HISTORY_RecoverSuccess", {count: result}), {style: "success"});
            }
        } catch (error: any) {
            showNotification(error);
        } finally {
            setIsLoading(false);
        }
    }

    return {
        comments: {get: comments.get},
        currentIndex,
        isLoading,
        handlers: {
            handleNavigate,
            handleHideToCurrent,
            handleHideAll,
            handleUnhideComments,
        }
    };
}
